/**
 * Process (F6) console API (uc-core src/modules/process/controllers):
 *   GET  /process-runs ?status&locationId&equipmentId&cropId&stepCode&stream&from&to&page&limit   (v_run_balance rows)
 *   GET  /process-runs/:id · /:id/inputs · /:id/balance
 *   POST /process-runs/:id/balance-check { recheckBandId? }                       process-runs:correct
 *   POST /process-runs/:id/corrections { exceptionId, kind, reasonCode, … }        process-runs:correct
 *   POST /exceptions/:id/resolve { resolutionCode (cause), note?, decision, recheckBandId? }   exceptions:resolve
 *   GET  /yield-bands ?cropId&stepCode&equipmentId · /current · /history · /:id · POST /yield-bands   yield-bands:*
 *   GET  /machines ?siteId&locationId&unlabelled · POST /equipment/:id/label/void { reason }        equipment:void-label
 *   GET  /batches ?runId&unitType&stream&hold&status&cropId&locationId&search · /batches/:id
 *   GET  /units/:id/trace-back · /trace-forward ?maxDepth
 * Weights come as kg strings (sacks sometimes as numbers); percents as "70.00". The console keeps
 * grams and hundredths of a percent (basis points) as integers.
 */
import { useQuery } from '@tanstack/react-query'
import { api, isApiError } from '@/api'
import { getList, getOne, getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { issueRequest } from '../model/process/issuePlan'
import { ck } from './keys'
import { mapExceptionLite, type ExceptionLite } from './gate'
import {
  arr,
  bool,
  decimalToMinor,
  iso,
  kgToGrams,
  kgToGrams0,
  num,
  numOrNull,
  obj,
  reasons,
  str,
  strOrNull,
} from './mappers'

/** Query keys for this area (prefix ['console','process', …]). */
export const pk = {
  all: ['console', 'process'] as const,
  runs: ['console', 'process', 'runs'] as const,
  bands: ['console', 'process', 'bands'] as const,
  machines: ['console', 'process', 'machines'] as const,
  batches: ['console', 'process', 'batches'] as const,
  trace: ['console', 'process', 'trace'] as const,
}

// ============================== words ==============================

export const STEP_CODES = ['CLEAN', 'DRY', 'DEHULL', 'SPLIT', 'POLISH', 'GRADE', 'SORT', 'PACK', 'DELABEL'] as const
export const STEP_LABEL: Record<string, string> = {
  CLEAN: 'Clean',
  DRY: 'Dry',
  DEHULL: 'Dehull',
  SPLIT: 'Split',
  POLISH: 'Polish',
  GRADE: 'Grade',
  SORT: 'Sort',
  PACK: 'Pack',
  DELABEL: 'Delabel',
}
const humanize = (code: string) => {
  const s = code.replace(/_/g, ' ').toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}
export const stepLabel = (code: string | null | undefined) => (code ? (STEP_LABEL[code] ?? humanize(code)) : '—')

export const RUN_STATUSES = ['OPEN', 'RUNNING', 'CLOSED', 'BALANCED', 'EXCEPTION', 'CANCELLED'] as const

/** Why a balance failed (detail.kinds / balance.kinds). */
export const BALANCE_KIND_LABEL: Record<string, string> = {
  RESIDUAL: 'Too much left over',
  YIELD: 'Yield outside the range',
  SURPLUS: 'More came out than went in',
  EXACT_ZERO: 'Left over is exactly zero',
}

/** Investigation causes (stored as the problem's resolution code). */
export const RUN_CAUSES = [
  'UNWEIGHED_SACK',
  'SCALE_ERROR',
  'TYPING_ERROR',
  'BAG_IN_WITHOUT_SCAN',
  'HIGH_MOISTURE',
  'MACHINE_FAULT',
  'BAND_WRONG',
  'UNEXPLAINED_LOSS',
  'MIXING',
] as const
export type RunCause = (typeof RUN_CAUSES)[number]
export const CAUSE_LABEL: Record<RunCause, string> = {
  UNWEIGHED_SACK: 'A sack was not weighed',
  SCALE_ERROR: 'Scale error',
  TYPING_ERROR: 'Typing error',
  BAG_IN_WITHOUT_SCAN: 'A bag went in without a scan',
  HIGH_MOISTURE: 'High moisture',
  MACHINE_FAULT: 'Machine fault',
  BAND_WRONG: 'The range is wrong for this machine',
  UNEXPLAINED_LOSS: 'Loss not explained',
  MIXING: 'Mixing',
}

export const RUN_DECISIONS = ['CORRECTED', 'ACCEPT_SHORTFALL', 'MOVE_TO_CONV'] as const
export type RunDecision = (typeof RUN_DECISIONS)[number]
export const DECISION_LABEL: Record<RunDecision, string> = {
  CORRECTED: 'Balanced after correction',
  ACCEPT_SHORTFALL: 'Accept the shortfall with its reason',
  MOVE_TO_CONV: 'Move all outputs to ordinary',
}

export const CORRECTION_KINDS = ['REWEIGH_OUTPUT', 'ADD_OUTPUT', 'ADD_INPUT', 'VOID_OUTPUT'] as const
export type CorrectionKind = (typeof CORRECTION_KINDS)[number]
export const CORRECTION_KIND_LABEL: Record<CorrectionKind, string> = {
  REWEIGH_OUTPUT: 'Weigh an output sack again',
  ADD_OUTPUT: 'Add a sack that was not recorded',
  ADD_INPUT: 'Add a bag that went in without a scan',
  VOID_OUTPUT: 'Void an output sack',
}

/** detail.kind of run problems, in plain words. */
export const PROBLEM_KIND_LABEL: Record<string, string> = {
  NO_SETTINGS: 'No range set for this crop and machine',
  CONCURRENT_RUNS: 'Two runs on the machine at once',
  STALE_PREVIOUS_RUN: 'Previous run on this machine was not finished',
  ORIGIN_CONFLICT: 'Bags from another origin',
  INPUT_NOT_AVAILABLE: 'A bag was not available',
  INPUT_ON_HOLD: 'A bag on hold went in',
  INPUT_BEFORE_CLEANOUT: 'A bag went in before the cleanout',
  ALLERGEN_CLEAN_MISSING: 'Allergen clean not confirmed',
  FLUSH_SHORT: 'Flush below the minimum',
  NOT_ISSUED: 'A bag went in that was not issued',
  REPORTED: 'Mixing reported at the machine',
  ISSUED_NOT_IN: 'An issued bag was not scanned in',
  STREAM_MISSING: 'An output was not recorded',
  OUTPUT_SACK_TAG: 'Problem with a sack tag',
  CANCEL_AFTER_INPUT: 'Cancelled after bags went in',
  RUN_LEFT_OPEN: 'Run left open too long',
  ISSUED_NOT_PUT_BACK: 'Issued bags not put back',
}
export function problemKindText(detail: Record<string, unknown>): string | null {
  const kinds = arr<string>(detail.kinds).map(String)
  const list = kinds.length ? kinds : typeof detail.kind === 'string' ? [detail.kind] : []
  if (!list.length) return null
  return list.map((k) => PROBLEM_KIND_LABEL[k] ?? BALANCE_KIND_LABEL[k] ?? humanize(k)).join(' · ')
}

export const UNIT_TYPE_LABEL: Record<string, string> = {
  LOT: 'Lot',
  BATCH: 'Batch',
  BYPRODUCT: 'By-product',
  REJECT: 'Reject',
  PACK: 'Pack unit',
}
export const unitTypeLabel = (t: string | null | undefined) => (t ? (UNIT_TYPE_LABEL[t] ?? humanize(t)) : '—')

/** Link for a unit inside the console: lots have their own page, everything else is a batch. */
export const unitHref = (unitId: string, unitType: string | null | undefined) =>
  unitType === 'LOT' ? `/c/lots/${unitId}` : `/c/batches/${unitId}`

// ============================== numbers ==============================

/** "70.00" → 7000 (hundredths of a percent); null when missing. */
export const pctToBp = (v: unknown): number | null =>
  typeof v === 'string' || typeof v === 'number' ? decimalToMinor(v, 2) : null
/** 7000 → "70.00%". */
export function bpText(bp: number | null | undefined, digits = 2): string {
  if (bp === null || bp === undefined) return '—'
  return `${(bp / 100).toFixed(digits)}%`
}
/** Signed kg string ("-0.600") → grams. */
const signedGrams = (v: unknown): number | null =>
  typeof v === 'string' || typeof v === 'number' ? kgToGrams(v) : null

// ============================== balance ==============================

export interface BandRef {
  id: string | null
  version: number | null
  minBp: number | null
  maxBp: number | null
}

export interface RunBalance {
  inputG: number
  flushG: number
  productG: number
  byproductG: number
  rejectG: number
  outputG: number
  /** input − output; negative is a surplus. */
  residualG: number
  residualBp: number | null
  /** product ÷ (input − flush). */
  yieldBp: number | null
  band: BandRef | null
  lossLimitBp: number | null
  /** RESIDUAL | YIELD | SURPLUS | EXACT_ZERO (balance view only). */
  kinds: string[]
  /** BALANCED | EXCEPTION | null (not checked yet). */
  result: string | null
  exceptionId: string | null
  status: string | null
}

/** Balance from GET /process-runs/:id/balance, the run detail's `balance`, or a v_run_balance row. */
export function mapBalance(r: Record<string, unknown>): RunBalance {
  const nested = r.band && typeof r.band === 'object' ? obj(r.band) : null
  const bandId = nested ? strOrNull(nested.id) : strOrNull(r.band_id)
  const minBp = pctToBp(nested ? nested.min_pct : r.min_pct)
  const maxBp = pctToBp(nested ? nested.max_pct : r.max_pct)
  const band: BandRef | null =
    bandId || minBp !== null
      ? { id: bandId, version: numOrNull(nested ? nested.version : r.band_version), minBp, maxBp }
      : null
  return {
    inputG: kgToGrams0(r.input_kg as string),
    flushG: kgToGrams0(r.flush_kg as string),
    productG: kgToGrams0(r.product_kg as string),
    byproductG: kgToGrams0(r.byproduct_kg as string),
    rejectG: kgToGrams0(r.reject_kg as string),
    outputG: kgToGrams0(r.output_kg as string),
    residualG: signedGrams(r.residual_kg) ?? 0,
    residualBp: pctToBp(r.residual_pct),
    yieldBp: pctToBp(r.yield_pct),
    band,
    lossLimitBp: pctToBp(r.loss_limit_pct),
    kinds: arr<string>(r.kinds).map(String),
    result: strOrNull(r.result),
    exceptionId: strOrNull(r.exception_id),
    status: strOrNull(r.status),
  }
}

export type YieldVerdict = 'IN_BAND' | 'BELOW' | 'ABOVE' | 'NO_BAND' | 'NOTHING_IN'
export type LeftOverVerdict = 'WITHIN' | 'OVER' | 'SURPLUS' | 'EXACT_ZERO' | 'NOTHING_IN' | 'NO_LIMIT'

/**
 * The balance rule on whole grams (same as the server's): left over within the loss limit and not
 * exactly zero; product ÷ (input − flush) inside the range. For the in-band / out-of-band picture;
 * the server's `result` stays the verdict of record.
 */
export function balanceVerdict(b: RunBalance): { yield: YieldVerdict; leftOver: LeftOverVerdict; passes: boolean } {
  let leftOver: LeftOverVerdict
  if (b.residualG === 0) leftOver = 'EXACT_ZERO'
  else if (b.inputG <= 0) leftOver = 'NOTHING_IN'
  else if (b.lossLimitBp === null) leftOver = 'NO_LIMIT'
  else if (Math.abs(b.residualG) * 10000 > b.lossLimitBp * b.inputG) leftOver = b.residualG > 0 ? 'OVER' : 'SURPLUS'
  else leftOver = 'WITHIN'

  const base = b.inputG - b.flushG
  let y: YieldVerdict
  if (base <= 0) y = 'NOTHING_IN'
  else if (!b.band || b.band.minBp === null || b.band.maxBp === null) y = 'NO_BAND'
  else if (b.productG * 10000 < b.band.minBp * base) y = 'BELOW'
  else if (b.productG * 10000 > b.band.maxBp * base) y = 'ABOVE'
  else y = 'IN_BAND'
  return { yield: y, leftOver, passes: y === 'IN_BAND' && leftOver === 'WITHIN' }
}

// ============================== runs ==============================

export interface PersonRef {
  id: string
  name: string
}
const mapPerson = (v: unknown): PersonRef | null => {
  const o = obj(v)
  if (!o.id) return null
  return { id: str(o.id), name: str(o.name).trim() || str(o.username) }
}

export interface RunRow {
  id: string
  code: string
  status: string
  stream: string
  stepCode: string
  cropId: string | null
  locationId: string | null
  locationCode: string | null
  locationName: string | null
  equipmentId: string | null
  equipmentCode: string | null
  operator: PersonRef | null
  originUnitId: string | null
  originUnitCode: string | null
  originFarmerId: string | null
  originFarmerCode: string | null
  seasonCode: string | null
  openedAt: string | null
  startedAt: string | null
  endedAt: string | null
  checkedAt: string | null
  bagsIn: number
  outputSacks: number
  balance: RunBalance
  openProblems: number
}

export function mapRunRow(r: Record<string, unknown>): RunRow {
  return {
    id: str(r.run_id ?? r.id),
    code: str(r.run_code ?? r.code),
    status: str(r.status),
    stream: str(r.stream, 'MAIN'),
    stepCode: str(r.step_code),
    cropId: strOrNull(r.crop_id),
    locationId: strOrNull(r.location_id),
    locationCode: strOrNull(r.location_code),
    locationName: strOrNull(r.location_name),
    equipmentId: strOrNull(r.equipment_id),
    equipmentCode: strOrNull(r.equipment_code),
    operator: mapPerson(r.operator) ?? (r.operator_id ? { id: str(r.operator_id), name: '' } : null),
    originUnitId: strOrNull(r.origin_unit_id),
    originUnitCode: strOrNull(r.origin_unit_code),
    originFarmerId: strOrNull(r.origin_farmer_id),
    originFarmerCode: strOrNull(r.origin_farmer_code),
    seasonCode: strOrNull(r.season_code),
    openedAt: iso(r.opened_at),
    startedAt: iso(r.started_at),
    endedAt: iso(r.ended_at),
    checkedAt: iso(r.checked_at),
    bagsIn: num(r.inputs),
    outputSacks: num(r.output_sacks),
    balance: mapBalance(r),
    openProblems: num(r.open_exceptions),
  }
}

export interface RunFilters extends PageParams {
  status?: string
  locationId?: string
  equipmentId?: string
  cropId?: string
  stepCode?: string
  stream?: string
  /** IST dates YYYY-MM-DD. */
  from?: string
  to?: string
}
export function useRuns(f: RunFilters, enabled = true) {
  return usePageQuery([...pk.runs, 'list', f], () => getPage('/process-runs', f, mapRunRow), { enabled })
}

export interface StepOutputDef {
  key: string
  label: string
  kind: string
  unitType: string
}
export interface RunStep {
  code: string
  label: string
  outputs: StepOutputDef[]
  cleanoutPhotoPoints: string[]
  lossLimitBp: number | null
}

export interface RunInputBag {
  id: string
  unitId: string
  unitCode: string
  containerId: string | null
  tag: string | null
  weightG: number | null
  scannedAt: string | null
  scannedById: string | null
}
export interface IssuedNotIn {
  containerId: string
  unitId: string
  unitCode: string
  tag: string | null
  issueNetG: number | null
  shipmentId: string | null
  shipmentCode: string | null
  issuedAt: string | null
}
export interface RunInputs {
  scannedIn: RunInputBag[]
  issuedNotIn: IssuedNotIn[]
}
export function mapRunInputs(r: Record<string, unknown>): RunInputs {
  return {
    scannedIn: arr(r.scanned_in).map((x) => ({
      id: str(x.id),
      unitId: str(x.unit_id),
      unitCode: str(x.unit_code),
      containerId: strOrNull(x.container_id),
      tag: strOrNull(x.tag),
      weightG: kgToGrams(x.weight_kg as string),
      scannedAt: iso(x.scanned_at),
      scannedById: strOrNull(x.scanned_by_id),
    })),
    issuedNotIn: arr(r.issued_not_in).map((x) => ({
      containerId: str(x.container_id),
      unitId: str(x.unit_id),
      unitCode: str(x.unit_code),
      tag: strOrNull(x.tag),
      issueNetG: kgToGrams(x.issue_net_kg as string),
      shipmentId: strOrNull(x.shipment_id),
      shipmentCode: strOrNull(x.shipment_code),
      issuedAt: iso(x.issued_at),
    })),
  }
}

export interface OutputSack {
  containerId: string
  /** The tag bound to the sack; null when it clashed (see conflictTag). */
  tag: string | null
  conflictTag: string | null
  status: string
  stream: string
  netG: number | null
  tareG: number | null
  hold: string
  placeId: string | null
  placePath: string | null
}
export interface RunOutput {
  id: string
  code: string
  unitType: string
  stream: string
  status: string
  hold: string
  holdReasons: string[]
  grade: string | null
  farmerId: string | null
  seasonCode: string | null
  grossG: number | null
  tareG: number | null
  netG: number | null
  committedAt: string | null
  streamKey: string | null
  isFlush: boolean
  sacks: OutputSack[]
}
export interface RunLink {
  id: string
  parentUnitId: string
  parentCode: string
  childUnitId: string
  childCode: string
  qtyG: number | null
  linkType: string
  createdAt: string | null
}
export interface RunEvent {
  id: string
  bizStep: string
  subjectType: string | null
  subjectId: string | null
  occurredAt: string | null
  actorId: string | null
  captureMode: string | null
  payload: Record<string, unknown>
}
export interface RunProblem extends ExceptionLite {
  resolvedAt: string | null
  resolvedById: string | null
}
export interface RunPhoto {
  id: string
  label: string
}

export interface RunDetail {
  id: string
  code: string
  status: string
  stream: string
  stepCode: string
  step: RunStep
  cropId: string | null
  cropName: string | null
  locationId: string | null
  locationCode: string | null
  locationName: string | null
  siteId: string | null
  equipmentId: string | null
  equipmentCode: string | null
  equipmentName: string | null
  sharedUse: boolean
  operator: PersonRef | null
  cleanoutBy: PersonRef | null
  cleanoutConfirmedAt: string | null
  originUnitId: string | null
  originUnitCode: string | null
  originFarmerId: string | null
  originFarmerCode: string | null
  seasonCode: string | null
  previousRunId: string | null
  previousRunCode: string | null
  previousCropId: string | null
  flushRequired: boolean
  flushUnitId: string | null
  yieldBandId: string | null
  allergenCleanRequired: boolean
  openedAt: string | null
  startedAt: string | null
  endedAt: string | null
  params: Record<string, unknown>
  balance: RunBalance
  inputs: RunInputs
  outputs: RunOutput[]
  links: RunLink[]
  problems: RunProblem[]
  events: RunEvent[]
  cleanoutPhotos: RunPhoto[]
  allergenPhoto: string | null
}

const PHOTO_POINT_LABEL: Record<string, string> = { HOPPER: 'Hopper', DRUM: 'Drum', OUTLET: 'Outlet' }

export function mapRunDetail(r: Record<string, unknown>): RunDetail {
  const step = obj(r.step)
  const flushUnitId = strOrNull(r.flush_unit_id)
  const events: RunEvent[] = arr(r.events).map((e) => ({
    id: str(e.id),
    bizStep: str(e.biz_step),
    subjectType: strOrNull(e.subject_type),
    subjectId: strOrNull(e.subject_id),
    occurredAt: iso(e.occurred_at),
    actorId: strOrNull(e.actor_id),
    captureMode: strOrNull(e.capture_mode),
    payload: obj(e.payload),
  }))
  // The latest cleanout confirmation carries the photos (one per photo point) and the allergen photo.
  const cleanout = [...events].reverse().find((e) => e.bizStep === 'CLEANOUT_CONFIRMED')
  const ids = cleanout ? arr<string>(cleanout.payload.photo_ids).map(String) : []
  const points = cleanout ? arr<string>(cleanout.payload.photo_points).map(String) : []
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status),
    stream: str(r.stream, 'MAIN'),
    stepCode: str(r.step_code),
    step: {
      code: str(step.code, str(r.step_code)),
      label: str(step.label, stepLabel(str(r.step_code))),
      outputs: arr(step.outputs).map((o) => ({ key: str(o.key), label: str(o.label), kind: str(o.kind), unitType: str(o.unitType) })),
      cleanoutPhotoPoints: arr<string>(step.cleanoutPhotoPoints).map(String),
      lossLimitBp: pctToBp(step.lossLimitPct),
    },
    cropId: strOrNull(r.crop_id),
    cropName: strOrNull(r.crop_name),
    locationId: strOrNull(r.location_id),
    locationCode: strOrNull(r.location_code),
    locationName: strOrNull(r.location_name),
    siteId: strOrNull(r.site_id),
    equipmentId: strOrNull(r.equipment_id),
    equipmentCode: strOrNull(r.equipment_code),
    equipmentName: strOrNull(r.equipment_name),
    sharedUse: bool(r.shared_use),
    operator: mapPerson(r.operator),
    cleanoutBy: mapPerson(r.cleanout_by),
    cleanoutConfirmedAt: iso(r.cleanout_confirmed_at),
    originUnitId: strOrNull(r.origin_unit_id),
    originUnitCode: strOrNull(r.origin_unit_code),
    originFarmerId: strOrNull(r.origin_farmer_id),
    originFarmerCode: strOrNull(r.origin_farmer_code),
    seasonCode: strOrNull(r.season_code),
    previousRunId: strOrNull(r.previous_run_id),
    previousRunCode: strOrNull(r.previous_run_code),
    previousCropId: strOrNull(r.previous_crop_id),
    flushRequired: bool(r.flush_required),
    flushUnitId,
    yieldBandId: strOrNull(r.yield_band_id),
    allergenCleanRequired: bool(r.allergen_clean_required),
    openedAt: iso(r.opened_at),
    startedAt: iso(r.started_at),
    endedAt: iso(r.ended_at),
    params: obj(r.params),
    balance: mapBalance(obj(r.balance)),
    inputs: mapRunInputs(obj(r.inputs)),
    outputs: arr(r.outputs).map((o) => ({
      id: str(o.id),
      code: str(o.code),
      unitType: str(o.unit_type),
      stream: str(o.stream, 'MAIN'),
      status: str(o.status),
      hold: str(o.hold, 'NONE'),
      holdReasons: reasons(o.hold_reason),
      grade: strOrNull(o.grade),
      farmerId: strOrNull(o.farmer_id),
      seasonCode: strOrNull(o.season_code),
      grossG: kgToGrams(o.gross_kg as string),
      tareG: kgToGrams(o.tare_kg as string),
      netG: kgToGrams(o.net_kg as string),
      committedAt: iso(o.committed_at),
      streamKey: strOrNull(o.stream_key),
      isFlush: str(o.stream_key) === 'FLUSH' || (flushUnitId !== null && str(o.id) === flushUnitId),
      sacks: arr(o.sacks).map(mapSack),
    })),
    links: arr(r.links).map((l) => ({
      id: str(l.id),
      parentUnitId: str(l.parent_unit_id),
      parentCode: str(l.parent_code),
      childUnitId: str(l.child_unit_id),
      childCode: str(l.child_code),
      qtyG: kgToGrams(l.qty_kg as string),
      linkType: str(l.link_type),
      createdAt: iso(l.created_at),
    })),
    problems: arr(r.exceptions).map((x) => ({
      ...mapExceptionLite(x),
      resolvedAt: iso(x.resolved_at),
      resolvedById: strOrNull(x.resolved_by_id),
    })),
    events,
    cleanoutPhotos: ids.map((id, i) => ({
      id,
      label: points[i] ? `Cleanout · ${PHOTO_POINT_LABEL[points[i]!] ?? humanize(points[i]!)}` : `Cleanout photo ${i + 1}`,
    })),
    allergenPhoto: cleanout ? strOrNull(cleanout.payload.allergen_photo_id) : null,
  }
}

const mapSack = (s: Record<string, unknown>): OutputSack => ({
  containerId: str(s.container_id),
  tag: strOrNull(s.tag),
  conflictTag: strOrNull(s.conflict_qr_payload),
  status: str(s.status),
  stream: str(s.stream, 'MAIN'),
  netG: kgToGrams((s.net_kg ?? s.net_kg_current) as string),
  tareG: kgToGrams(s.tare_kg as string),
  hold: str(s.hold, 'NONE'),
  placeId: strOrNull(s.place_id),
  placePath: strOrNull(s.place_path),
})

export interface LineageGroup {
  childUnitId: string
  childCode: string
  parents: Array<{ unitId: string; code: string; qtyG: number | null }>
}
/** Links grouped by output: each output with the lots / batches it came from. */
export function lineageByOutput(links: RunLink[]): LineageGroup[] {
  const by = new Map<string, LineageGroup>()
  for (const l of links) {
    const g = by.get(l.childUnitId) ?? { childUnitId: l.childUnitId, childCode: l.childCode, parents: [] }
    if (!g.parents.some((p) => p.unitId === l.parentUnitId)) g.parents.push({ unitId: l.parentUnitId, code: l.parentCode, qtyG: l.qtyG })
    by.set(l.childUnitId, g)
  }
  return [...by.values()]
}

export function useRun(id: string | undefined) {
  return useQuery({
    queryKey: [...pk.runs, 'detail', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/process-runs/${id}`, mapRunDetail),
  })
}

export interface CorrectionBody {
  exceptionId: string
  kind: CorrectionKind
  reasonCode: RunCause
  qrPayload?: string
  containerId?: string
  weightReadG?: number
  sackTypeId?: string
  streamKey?: string
}
const RUN_TOUCHED = [pk.all, ck.exceptions, ck.stock, ck.dashboard]

/** `onError` returning true keeps the refusal in the dialog (no toast). */
export function useRunCorrection(runId: string, onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<CorrectionBody, Record<string, unknown>>({
    mutationFn: (body) => api.post(`/process-runs/${runId}/corrections`, body),
    invalidate: RUN_TOUCHED,
    success: 'Correction recorded — balance checked again',
    errorTitle: 'Correction not saved',
    onError,
  })
}

export function useBalanceCheck(runId: string, onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<{ recheckBandId?: string }, Record<string, unknown>>({
    mutationFn: (body) => api.post(`/process-runs/${runId}/balance-check`, body.recheckBandId ? body : {}),
    invalidate: RUN_TOUCHED,
    success: (d) => (str(obj(d).result) === 'BALANCED' ? 'Balance checked — it passes now' : 'Balance checked — still out of range'),
    errorTitle: 'Not re-checked',
    onError,
  })
}

export interface DecisionBody {
  exceptionId: string
  cause: RunCause
  decision: RunDecision
  note?: string
  recheckBandId?: string
}
/** The run decision through the exceptions resolve endpoint (the cause is the resolution code). */
export function useRunDecision(onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<DecisionBody, Record<string, unknown>>({
    mutationFn: ({ exceptionId, cause, decision, note, recheckBandId }) =>
      api.post(`/exceptions/${exceptionId}/resolve`, {
        resolutionCode: cause,
        decision,
        note: note || undefined,
        recheckBandId: recheckBandId || undefined,
      }),
    invalidate: [...RUN_TOUCHED, ck.lots],
    success: (_d, v) =>
      v.decision === 'MOVE_TO_CONV'
        ? 'Problem closed — outputs moved to ordinary'
        : v.decision === 'ACCEPT_SHORTFALL'
          ? 'Problem closed — shortfall accepted'
          : 'Problem closed — run balanced',
    errorTitle: 'Problem not closed',
    onError,
  })
}

// ============================== yield bands ==============================

export interface BandRow {
  id: string
  cropId: string
  cropVarietyId: string | null
  stepCode: string
  equipmentId: string | null
  minBp: number | null
  maxBp: number | null
  version: number
  /** YYYY-MM-DD */
  effectiveFrom: string | null
  source: string | null
  minFlushG: number | null
  minInputG: number | null
  lossLimitBp: number | null
  approvedById: string | null
  changeNote: string | null
  createdAt: string | null
}
export function mapBand(r: Record<string, unknown>): BandRow {
  return {
    id: str(r.id),
    cropId: str(r.crop_id),
    cropVarietyId: strOrNull(r.crop_variety_id),
    stepCode: str(r.step_code),
    equipmentId: strOrNull(r.equipment_id),
    minBp: pctToBp(r.min_pct),
    maxBp: pctToBp(r.max_pct),
    version: num(r.version, 1),
    effectiveFrom: strOrNull(r.effective_from)?.slice(0, 10) ?? null,
    source: strOrNull(r.source),
    minFlushG: kgToGrams(r.min_flush_kg as string),
    minInputG: kgToGrams(r.min_input_kg as string),
    lossLimitBp: pctToBp(r.max_unaccounted_pct),
    approvedById: strOrNull(r.approved_by_id),
    changeNote: strOrNull(r.change_note),
    createdAt: iso(r.created_at),
  }
}

export interface BandFilters extends PageParams {
  cropId?: string
  stepCode?: string
  equipmentId?: string
}
export function useBands(f: BandFilters, enabled = true) {
  return usePageQuery([...pk.bands, 'list', f], () => getPage('/yield-bands', f, mapBand), { enabled })
}
export function useBand(id: string | null | undefined) {
  return useQuery({
    queryKey: [...pk.bands, 'one', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/yield-bands/${id}`, mapBand),
    staleTime: 5 * 60_000,
  })
}
export interface BandLookup {
  cropId: string
  stepCode: string
  equipmentId?: string
  cropVarietyId?: string
}
/** The row a new run would use; null when there is none (404). */
export function useCurrentBand(q: BandLookup | null) {
  return useQuery({
    queryKey: [...pk.bands, 'current', q],
    enabled: Boolean(q?.cropId && q.stepCode),
    retry: false,
    queryFn: async () => {
      try {
        return await getOne('/yield-bands/current', mapBand, q!)
      } catch (e) {
        if (isApiError(e) && e.status === 404) return null
        throw e
      }
    },
  })
}

export interface BandHistoryRow {
  runId: string
  runCode: string
  status: string
  stream: string
  equipmentId: string | null
  endedAt: string | null
  inputG: number | null
  outputG: number | null
  residualG: number | null
  residualBp: number | null
  yieldBp: number | null
  bandId: string | null
  bandVersion: number | null
  minBp: number | null
  maxBp: number | null
  lossLimitBp: number | null
}
export function mapBandHistory(r: Record<string, unknown>): BandHistoryRow {
  return {
    runId: str(r.run_id),
    runCode: str(r.run_code),
    status: str(r.status),
    stream: str(r.stream, 'MAIN'),
    equipmentId: strOrNull(r.equipment_id),
    endedAt: iso(r.ended_at),
    inputG: kgToGrams(r.input_kg as string),
    outputG: kgToGrams(r.output_kg as string),
    residualG: signedGrams(r.residual_kg),
    residualBp: pctToBp(r.residual_pct),
    yieldBp: pctToBp(r.yield_pct),
    bandId: strOrNull(r.band_id),
    bandVersion: numOrNull(r.band_version),
    minBp: pctToBp(r.min_pct),
    maxBp: pctToBp(r.max_pct),
    lossLimitBp: pctToBp(r.loss_limit_pct),
  }
}
export function useBandHistory(q: (BandLookup & { limit?: number }) | null) {
  return useQuery({
    queryKey: [...pk.bands, 'history', q],
    enabled: Boolean(q?.cropId && q.stepCode),
    queryFn: () => getList('/yield-bands/history', q!, mapBandHistory),
  })
}

export interface LoadBandBody {
  cropId: string
  cropVarietyId?: string
  stepCode: string
  equipmentId?: string
  minPct: string
  maxPct: string
  minFlushKg?: string
  minInputKg?: string
  effectiveFrom?: string
  source?: 'MANUAL' | 'LEARNED'
  changeNote?: string
  approvedById?: string
}
export function useLoadBand(onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<LoadBandBody, Record<string, unknown>>({
    mutationFn: (body) => api.post('/yield-bands', body),
    invalidate: [pk.bands, pk.machines],
    success: (d) => `Version ${num(obj(d).version, 1)} saved`,
    errorTitle: 'Range not saved',
    onError,
  })
}

// ============================== machines ==============================

export interface MachineRow {
  id: string
  code: string
  name: string
  category: string | null
  serialNo: string | null
  sharedUse: boolean
  active: boolean
  locationId: string | null
  siteId: string | null
  locationPath: string | null
  stepCodes: string[]
  capacityKgH: number | null
  qrCodeId: string | null
  labelPayload: string | null
  settings: BandRow[]
  lastRun: { id: string; code: string; status: string; stream: string; cropId: string | null; stepCode: string; createdAt: string | null } | null
}
export function mapMachine(r: Record<string, unknown>): MachineRow {
  const last = r.last_run ? obj(r.last_run) : null
  return {
    id: str(r.id),
    code: str(r.code),
    name: str(r.name),
    category: strOrNull(r.category),
    serialNo: strOrNull(r.serial_no),
    sharedUse: bool(r.shared_use),
    active: r.is_active === undefined ? true : bool(r.is_active),
    locationId: strOrNull(r.location_id),
    siteId: strOrNull(r.site_id),
    locationPath: strOrNull(r.location_path),
    stepCodes: arr<string>(r.step_codes).map(String),
    capacityKgH: numOrNull(r.capacity_kg_h),
    qrCodeId: strOrNull(r.qr_code_id),
    labelPayload: strOrNull(r.label_payload),
    settings: arr(r.settings).map((s) => mapBand({ ...s, equipment_id: s.equipment_id ?? r.id })),
    lastRun: last
      ? {
          id: str(last.id),
          code: str(last.code),
          status: str(last.status),
          stream: str(last.stream, 'MAIN'),
          cropId: strOrNull(last.crop_id),
          stepCode: str(last.step_code),
          createdAt: iso(last.created_at),
        }
      : null,
  }
}
export interface MachineFilters extends PageParams {
  siteId?: string
  locationId?: string
  unlabelled?: boolean
}
export function useMachines(f: MachineFilters, enabled = true) {
  return usePageQuery([...pk.machines, 'list', f], () => getPage('/machines', f, mapMachine), { enabled })
}
/** All machines (pickers). */
export function useAllMachines() {
  return useQuery({
    queryKey: [...pk.machines, 'all'],
    queryFn: () => getPage('/machines', { page: 1, limit: 200 }, mapMachine).then((p) => p.rows),
    staleTime: 5 * 60_000,
  })
}
export function useVoidMachineLabel() {
  return useConsoleMutation<{ id: string; reason: string }>({
    mutationFn: ({ id, reason }) => api.post(`/equipment/${id}/label/void`, { reason }),
    invalidate: [pk.machines, ck.equipment],
    success: 'Label voided — the machine needs a new label',
    errorTitle: 'Label not voided',
  })
}

// ============================== batches ==============================

export interface BatchRow {
  id: string
  code: string
  unitType: string
  stream: string
  status: string
  hold: string
  holdReasons: string[]
  grade: string | null
  cropId: string | null
  farmerId: string | null
  farmerCode: string | null
  seasonCode: string | null
  netG: number | null
  bagCount: number
  committedAt: string | null
  runId: string | null
  runCode: string | null
  stepCode: string | null
  runStatus: string | null
  locationId: string | null
  isFlush: boolean
}
export function mapBatchRow(r: Record<string, unknown>): BatchRow {
  return {
    id: str(r.id),
    code: str(r.code),
    unitType: str(r.unit_type),
    stream: str(r.stream, 'MAIN'),
    status: str(r.status),
    hold: str(r.hold, 'NONE'),
    holdReasons: reasons(r.hold_reason),
    grade: strOrNull(r.grade),
    cropId: strOrNull(r.crop_id),
    farmerId: strOrNull(r.farmer_id),
    farmerCode: strOrNull(r.farmer_code),
    seasonCode: strOrNull(r.season_code),
    netG: kgToGrams(r.net_kg as string),
    bagCount: num(r.bag_count),
    committedAt: iso(r.committed_at),
    runId: strOrNull(r.run_id),
    runCode: strOrNull(r.run_code),
    stepCode: strOrNull(r.step_code),
    runStatus: strOrNull(r.run_status),
    locationId: strOrNull(r.location_id),
    isFlush: bool(r.is_flush),
  }
}

export interface BatchRelative {
  unitId: string
  code: string
  unitType: string
  stream: string
  status: string | null
  farmerId: string | null
  runId: string | null
  via: string
  qtyG: number | null
  linkType: string | null
}
const mapRelative = (r: Record<string, unknown>): BatchRelative => ({
  unitId: str(r.unit_id),
  code: str(r.code),
  unitType: str(r.unit_type),
  stream: str(r.stream, 'MAIN'),
  status: strOrNull(r.status),
  farmerId: strOrNull(r.farmer_id),
  runId: strOrNull(r.run_id),
  via: str(r.via),
  qtyG: kgToGrams(r.qty_kg as string),
  linkType: strOrNull(r.link_type),
})
export interface BatchSample {
  id: string
  kind: string
  status: string
  seal: string | null
  weightG: number | null
  collectedAt: string | null
  labOrgId: string | null
  sentAt: string | null
  panelRequested: string | null
}
export interface BatchDetail extends BatchRow {
  cropName: string | null
  cropVarietyId: string | null
  cultivationId: string | null
  grossG: number | null
  tareG: number | null
  equipmentId: string | null
  singleOrigin: boolean
  parents: BatchRelative[]
  children: BatchRelative[]
  sacks: OutputSack[]
  samples: BatchSample[]
  problems: ExceptionLite[]
}
export function mapBatchDetail(r: Record<string, unknown>): BatchDetail {
  return {
    ...mapBatchRow(r),
    cropName: strOrNull(r.crop_name),
    cropVarietyId: strOrNull(r.crop_variety_id),
    cultivationId: strOrNull(r.cultivation_id),
    grossG: kgToGrams(r.gross_kg as string),
    tareG: kgToGrams(r.tare_kg as string),
    equipmentId: strOrNull(r.equipment_id),
    singleOrigin: bool(r.single_origin),
    parents: arr(r.parents).map(mapRelative),
    children: arr(r.children).map(mapRelative),
    sacks: arr(r.sacks).map(mapSack),
    samples: arr(r.samples).map((s) => ({
      id: str(s.id),
      kind: str(s.kind),
      status: str(s.status),
      seal: strOrNull(s.seal),
      weightG: numOrNull(s.weight_g) === null ? null : Math.round(num(s.weight_g)),
      collectedAt: iso(s.collected_at),
      labOrgId: strOrNull(s.lab_org_id),
      sentAt: iso(s.sent_at),
      panelRequested: strOrNull(s.panel_requested),
    })),
    problems: arr(r.exceptions).map(mapExceptionLite),
  }
}

export interface BatchFilters extends PageParams {
  runId?: string
  unitType?: string
  stream?: string
  hold?: string
  status?: string
  cropId?: string
  locationId?: string
  search?: string
}
export function useBatches(f: BatchFilters, enabled = true) {
  return usePageQuery([...pk.batches, 'list', f], () => getPage('/batches', f, mapBatchRow), { enabled })
}
export function useBatch(id: string | undefined) {
  return useQuery({
    queryKey: [...pk.batches, 'detail', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/batches/${id}`, mapBatchDetail),
  })
}

// ============================== trace ==============================

export interface TraceNode {
  unitId: string
  depth: number
  fromUnitId: string | null
  runId: string | null
  via: string
  qtyG: number | null
  linkType: string | null
  isCycle: boolean
  unitCode: string
  unitType: string
  stream: string
  status: string | null
  hold: string
  farmerId: string | null
  farmerCode: string | null
  cropId: string | null
  seasonCode: string | null
  netG: number | null
  /** The run this step went through (the link's run). */
  runCode: string | null
  runStatus: string | null
  sample: { sampleId: string; status: string; seal: string | null; labReportStatus: string | null; labOverall: string | null } | null
}
export interface TraceResult {
  unitId: string
  direction: 'back' | 'forward'
  maxDepth: number
  nodes: TraceNode[]
  sourceLotIds: string[]
  farmerIds: string[]
}
export function mapTraceNode(n: Record<string, unknown>): TraceNode {
  const s = n.sample ? obj(n.sample) : null
  return {
    unitId: str(n.unit_id),
    depth: num(n.depth),
    fromUnitId: strOrNull(n.from_unit_id),
    runId: strOrNull(n.run_id),
    via: str(n.via),
    qtyG: kgToGrams(n.qty_kg as string),
    linkType: strOrNull(n.link_type),
    isCycle: bool(n.is_cycle),
    unitCode: str(n.unit_code),
    unitType: str(n.unit_type),
    stream: str(n.stream, 'MAIN'),
    status: strOrNull(n.status),
    hold: str(n.hold, 'NONE'),
    farmerId: strOrNull(n.farmer_id),
    farmerCode: strOrNull(n.farmer_code),
    cropId: strOrNull(n.crop_id),
    seasonCode: strOrNull(n.season_code),
    netG: kgToGrams(n.net_kg as string),
    runCode: strOrNull(n.run_code),
    runStatus: strOrNull(n.run_status),
    sample: s
      ? {
          sampleId: str(s.sample_id),
          status: str(s.status),
          seal: strOrNull(s.seal),
          labReportStatus: strOrNull(s.lab_report_status),
          labOverall: strOrNull(s.lab_overall),
        }
      : null,
  }
}
export function mapTrace(r: Record<string, unknown>): TraceResult {
  return {
    unitId: str(r.unit_id),
    direction: str(r.direction) === 'forward' ? 'forward' : 'back',
    maxDepth: num(r.max_depth, 32),
    nodes: arr(r.nodes).map(mapTraceNode),
    sourceLotIds: arr<string>(r.source_lot_ids).map(String),
    farmerIds: arr<string>(r.farmer_ids).map(String),
  }
}

export interface TraceRow {
  node: TraceNode
  /** Other units at the previous depth this one is also reached from (codes). */
  alsoFrom: string[]
}
/**
 * The trace as an indented list: depth-first from the start unit, each unit shown once per depth
 * (a unit reached from two parents at the same depth is listed under the first, with the others
 * named in `alsoFrom`). Nodes the walk cannot place under a parent are appended in depth order.
 */
export function traceRows(nodes: TraceNode[]): TraceRow[] {
  const key = (n: { depth: number; unitId: string }) => `${n.depth}:${n.unitId}`
  const firstAt = new Map<string, TraceNode>()
  const fromCodes = new Map<string, Set<string>>()
  const codeOf = new Map<string, string>()
  for (const n of nodes) codeOf.set(n.unitId, n.unitCode)
  for (const n of nodes) {
    const k = key(n)
    if (!firstAt.has(k)) firstAt.set(k, n)
    if (n.fromUnitId) {
      const s = fromCodes.get(k) ?? new Set<string>()
      s.add(n.fromUnitId)
      fromCodes.set(k, s)
    }
  }
  const unique = [...firstAt.values()]
  const childrenOf = new Map<string, TraceNode[]>()
  for (const n of unique) {
    if (!n.fromUnitId) continue
    const pk2 = `${n.depth - 1}:${n.fromUnitId}`
    const list = childrenOf.get(pk2) ?? []
    list.push(n)
    childrenOf.set(pk2, list)
  }
  const out: TraceRow[] = []
  const placed = new Set<string>()
  const visit = (n: TraceNode) => {
    const k = key(n)
    if (placed.has(k)) return
    placed.add(k)
    const others = [...(fromCodes.get(k) ?? [])].filter((id) => id !== n.fromUnitId).map((id) => codeOf.get(id) ?? id)
    out.push({ node: n, alsoFrom: others })
    if (n.isCycle) return
    for (const c of (childrenOf.get(k) ?? []).sort((a, b) => a.unitCode.localeCompare(b.unitCode))) visit(c)
  }
  const roots = unique.filter((n) => n.depth === 0)
  for (const r of roots) visit(r)
  for (const n of [...unique].sort((a, b) => a.depth - b.depth)) if (!placed.has(key(n))) visit(n)
  return out
}

export function useTrace(unitId: string | undefined, direction: 'back' | 'forward', maxDepth = 32, enabled = true) {
  return useQuery({
    queryKey: [...pk.trace, unitId, direction, maxDepth],
    enabled: Boolean(unitId) && enabled,
    queryFn: () => getOne(`/units/${unitId}/trace-${direction}`, mapTrace, { maxDepth }),
  })
}

/** Plan an issue (the keeper's task on the phone): these lots' bags to this run. */
export function usePlanIssue(runId: string, onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<string[], Record<string, unknown>>({
    mutationFn: (unitIds) => api.post('/shipments', issueRequest(runId, unitIds)),
    invalidate: [...RUN_TOUCHED, ck.shipments],
    success: (d) => `Issue ${str(obj(d).code)} sent to the store keeper`,
    errorTitle: 'Issue not planned',
    onError,
  })
}
